import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { fmt12 } from "../lib/utils";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, MapPin, Sunrise, Sunset, Sparkles, CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function classifyShift(s) {
  const t = (s.title || "").toLowerCase();
  if (t.startsWith("morning shift")) return "morning";
  if (t.startsWith("evening shift")) return "evening";
  return "custom";
}

function shortChipLabel(s) {
  // Show a compact but recognisable label in the calendar cell.
  // "Morning Shift 1/2" → "AM 1"/"AM 2", "Morning Shift" → "AM",
  // "Evening Shift" → "Evening", anything else → the title itself.
  const t = (s.title || "").trim();
  const mNum = t.match(/^morning shift\s*(\d+)?$/i);
  if (mNum) return mNum[1] ? `AM ${mNum[1]}` : "AM";
  if (/^evening shift$/i.test(t)) return "Evening";
  return t;
}

export default function Shifts() {
  const nav = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [waiver, setWaiver] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, w] = await Promise.all([api.get("/shifts"), api.get("/waiver/me")]);
    setShifts(s.data);
    setWaiver(w.data);
  };
  useEffect(() => { load(); }, []);

  const hasEmergency = !!(waiver?.emergency_contact_name && waiver?.emergency_contact_phone);
  const isExpired = !!waiver?.is_expired;
  const isCleared = !!waiver?.signed && hasEmergency && !isExpired;
  const hasSignedUpShifts = shifts.some((s) => s.is_signed_up);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarLinks, setCalendarLinks] = useState(null);
  const [calendarStep, setCalendarStep] = useState("choose"); // "choose" | "google" | "apple" | "outlook"

  const openCalendarPicker = async () => {
    setCalendarStep("choose");
    if (calendarLinks) { setCalendarOpen(true); return; }
    try {
      const { data } = await api.get("/calendar/me");
      setCalendarLinks(data);
      setCalendarOpen(true);
    } catch { toast.error("Please sign in to sync your shifts."); nav("/login"); }
  };

  const proceedToCalendar = () => {
    if (!calendarLinks) return;
    let url = null;
    let newTab = false;
    if (calendarStep === "google") {
      url = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(calendarLinks.feed_url)}`;
      newTab = true;
    } else if (calendarStep === "apple") {
      url = calendarLinks.webcal_url;
    } else if (calendarStep === "outlook") {
      url = `https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(calendarLinks.feed_url)}&name=${encodeURIComponent("HRAAD Volunteer Shifts")}`;
      newTab = true;
    }
    if (!url) return;
    setCalendarOpen(false);
    if (newTab) window.open(url, "_blank");
    else window.location.href = url;
  };

  const downloadShiftIcs = async (shift, e) => {
    e?.stopPropagation?.();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/calendar/shift/${shift.shift_id}.ics`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hraad-${shift.date}-${(shift.start_time||"").replace(":","")}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Calendar event downloaded.");
    } catch { toast.error("Could not build calendar event."); }
  };

  // Direct-add URLs — no download, calendar app opens with event pre-filled.
  const _dt = (d, t) => `${d.replace(/-/g,"")}T${(t||"00:00").replace(":","")}00`;
  const _text = (s) => `${s.title}${s.role ? " — " + s.role : ""}`.trim();
  const _details = (s) => [s.description, "Hood River Adopt A Dog volunteer shift."].filter(Boolean).join("\n");
  const _loc = (s) => s.location || "3910 Heron Drive, Hood River, OR 97031";
  const googleAddUrl = (s) => {
    const p = new URLSearchParams({
      action: "TEMPLATE",
      text: _text(s),
      dates: `${_dt(s.date, s.start_time)}/${_dt(s.date, s.end_time)}`,
      details: _details(s),
      location: _loc(s),
      ctz: "America/Los_Angeles",
    });
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
  };
  const outlookAddUrl = (s) => {
    const p = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: _text(s),
      startdt: `${s.date}T${s.start_time}:00`,
      enddt: `${s.date}T${s.end_time}:00`,
      location: _loc(s),
      body: _details(s),
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
  };
  const appleAddUrl = (s) => `${process.env.REACT_APP_BACKEND_URL}/api/calendar/shift/${s.shift_id}.ics`;

  const byDate = useMemo(() => {
    const _today = ymd(new Date());
    const map = {};
    for (const s of shifts) {
      // Hide shifts on past dates from the Open Shifts view.
      if (s.date < _today) continue;
      (map[s.date] ||= []).push(s);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    }
    return map;
  }, [shifts]);

  const grid = useMemo(() => {
    const firstDow = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1 - firstDow);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const claim = async (shift) => {
    if (!waiver?.signed) {
      toast.error("Please sign the waiver first.");
      nav("/waiver");
      return;
    }
    if (isExpired) {
      toast.error("Your waiver has expired. Please re-sign it.");
      nav("/waiver");
      return;
    }
    if (!hasEmergency) {
      toast.error("Please add your emergency contact before signing up.");
      nav("/profile");
      return;
    }
    setBusy(true);
    try {
      await api.post("/shifts/signup", { shift_id: shift.shift_id });
      toast.success(`Signed up for ${shift.title} on ${shift.date}! Add it to your calendar below.`);
      // Keep the dialog open and instantly flip it to the "You're on this shift" view
      // with the three add-to-calendar buttons.
      setSelected({ ...shift, is_signed_up: true, signup_count: (shift.signup_count || 0) + 1 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancelSignup = async (shift) => {
    setBusy(true);
    try {
      await api.delete(`/shifts/signup/${shift.shift_id}`);
      toast.success("Signup cancelled.");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const todayStr = ymd(new Date());
  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const chipStyle = (kind, s) => {
    const base = "flex items-center justify-between gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold cursor-pointer transition-colors";
    if (s.is_signed_up) return `${base} bg-[#E4EBDD] text-[#2D4A3E] hover:bg-[#D6E0CB]`;
    const full = s.signup_count >= s.max_volunteers;
    if (full) return `${base} bg-[color:var(--cream-2)] text-[color:var(--stone)] cursor-not-allowed`;
    if (kind === "morning") return `${base} bg-[#F5D547]/80 hover:bg-[#F5D547] text-[color:var(--ink)]`;
    if (kind === "evening") return `${base} bg-[color:var(--ink)] text-[color:var(--cream)] hover:opacity-90`;
    return `${base} bg-[#EF3B3B]/20 text-[color:var(--red)] hover:bg-[#EF3B3B]/35`;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="tag-pill">Open Shifts</div>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-3">Pick your paws-on time.</h1>
            <p className="text-[color:var(--stone)] mt-2 max-w-2xl text-sm">Every day has three default slots — Morning 1, Morning 2, and Evening — plus any special events. Tap any chip to sign up or cancel.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="cal-prev-btn" onClick={()=>{ const d = new Date(cursor); d.setMonth(cursor.getMonth() - 1); setCursor(d); }} className="p-2 rounded-full border-2 border-[color:var(--border)] hover:border-[color:var(--ink)]" aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="font-display text-base sm:text-xl min-w-[140px] sm:min-w-[180px] text-center" data-testid="cal-month-label">{monthLabel}</div>
            <button data-testid="cal-next-btn" onClick={()=>{ const d = new Date(cursor); d.setMonth(cursor.getMonth() + 1); setCursor(d); }} className="p-2 rounded-full border-2 border-[color:var(--border)] hover:border-[color:var(--ink)]" aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button data-testid="cal-today-btn" onClick={()=>{ const d = new Date(); d.setDate(1); setCursor(d); }} className="btn-pill btn-ghost-ink text-xs">Today</button>
            {hasSignedUpShifts && (
              <button
                data-testid="shifts-subscribe-calendar-btn"
                onClick={openCalendarPicker}
                className="btn-pill btn-primary text-xs inline-flex items-center gap-1.5"
                title="Sync every shift you sign up for to your phone calendar"
              >
                <CalendarPlus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Add my shifts to calendar</span><span className="sm:hidden">Sync</span>
              </button>
            )}
          </div>
        </div>

        {!isCleared && (
          <div className="mb-6 card-warm p-5 border-l-4 border-l-[color:var(--red)]" data-testid="shifts-waiver-warning">
            {!waiver?.signed ? (
              <>
                <div className="font-display text-base">Sign the waiver to unlock sign-ups.</div>
                <p className="text-xs text-[color:var(--stone)] mt-1">You&rsquo;ll also need an emergency contact on file.</p>
                <button data-testid="shifts-sign-waiver-btn" onClick={()=>nav("/waiver")} className="mt-3 btn-pill btn-primary text-sm">Sign the waiver</button>
              </>
            ) : isExpired ? (
              <>
                <div className="font-display text-base">Your waiver has expired.</div>
                <p className="text-xs text-[color:var(--stone)] mt-1">Signed waivers are valid for 12 months. Please re-sign to keep volunteering.</p>
                <button data-testid="shifts-resign-waiver-btn" onClick={()=>nav("/waiver")} className="mt-3 btn-pill btn-primary text-sm">Re-sign waiver</button>
              </>
            ) : (
              <>
                <div className="font-display text-base">Add an emergency contact to unlock sign-ups.</div>
                <p className="text-xs text-[color:var(--stone)] mt-1">Your waiver is signed, but we still need someone to call in case of emergency.</p>
                <button data-testid="shifts-add-emergency-btn" onClick={()=>nav("/profile")} className="mt-3 btn-pill btn-primary text-sm">Add emergency contact</button>
              </>
            )}
          </div>
        )}
        {waiver?.is_expiring_soon && !isExpired && (
          <div className="mb-6 card-warm p-4 border-l-4 border-l-[color:var(--yellow)]" data-testid="shifts-waiver-expiring">
            <div className="text-sm">
              <span className="font-semibold">Heads up:</span> your waiver expires in {waiver.days_until_expiry} day{waiver.days_until_expiry === 1 ? "" : "s"}.
              <button onClick={()=>nav("/waiver")} className="ml-2 font-semibold text-[color:var(--red)] hover:underline">Re-sign now</button>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-2 mb-4 text-xs text-[color:var(--stone)]">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#F5D547]" /> Morning shifts</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[color:var(--ink)]" /> Evening shift</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#EF3B3B]/60" /> Special event</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[#E4EBDD] border border-[#2D4A3E]" /> You&rsquo;re signed up</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[color:var(--cream-2)] border border-[color:var(--border)]" /> Full</span>
        </div>

        {/* Mobile hint */}
        <p className="md:hidden text-[11px] text-[color:var(--stone)] mb-2">← Swipe the calendar to see the full week →</p>

        {/* Horizontal scroll wrapper for mobile */}
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[640px]">
            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {WEEK_LABELS.map((w) => (
                <div key={w} className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--stone)] text-center py-1">{w}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-2">
              {grid.map((d) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const dateStr = ymd(d);
                const isToday = dateStr === todayStr;
                const dayShifts = byDate[dateStr] || [];
                return (
                  <div
                    key={dateStr}
                    data-testid={`cal-cell-${dateStr}`}
                    className={`min-h-[110px] md:min-h-[130px] rounded-2xl p-2 md:p-2.5 border-2 flex flex-col ${inMonth ? "bg-white" : "bg-[color:var(--cream-2)]/40 opacity-70"} ${isToday ? "border-[color:var(--red)]" : "border-[color:var(--border)]"}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className={`text-sm font-semibold ${isToday ? "text-[color:var(--red)]" : "text-[color:var(--ink)]"}`}>{d.getDate()}</div>
                      {isToday && <span className="tag-pill tag-red !text-[9px] !py-0 !px-2">Today</span>}
                    </div>

                    <div className="flex flex-col gap-1 flex-1">
                      {dayShifts.map((s) => {
                        const kind = classifyShift(s);
                        const full = s.signup_count >= s.max_volunteers && !s.is_signed_up;
                        const Icon = kind === "morning" ? Sunrise : kind === "evening" ? Sunset : Sparkles;
                        return (
                          <div
                            key={s.shift_id}
                            data-testid={`cal-shift-${s.shift_id}`}
                            onClick={() => { if (!full) setSelected(s); }}
                            className={chipStyle(kind, s)}
                            title={`${s.title} · ${fmt12(s.start_time)}–${fmt12(s.end_time)}`}
                          >
                            <span className="flex items-center gap-1 truncate">
                              <Icon className="h-3 w-3 shrink-0" />
                              <span className="truncate">{shortChipLabel(s)}</span>
                            </span>
                            <span className="shrink-0 opacity-80 flex items-center gap-1">
                              {s.is_signed_up ? (
                                <><CalendarPlus className="h-3 w-3" /><span>✓</span></>
                              ) : `${s.signup_count}/${s.max_volunteers}`}
                            </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="shift-signup-dialog" className="rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {selected?.is_signed_up ? "You're on this shift — add it to your calendar" : "Confirm your shift"}
            </DialogTitle>
            <DialogDescription className="sr-only">Shift details and sign-up actions.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="mt-2 text-[color:var(--ink)] text-base">
              <div className="font-semibold text-lg">{selected.title}</div>
              <div className="text-sm text-[color:var(--stone)] mt-1">{selected.date} · {fmt12(selected.start_time)}–{fmt12(selected.end_time)}</div>
              <div className="text-sm text-[color:var(--stone)] flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" /> {selected.location}</div>
              <div className="text-sm text-[color:var(--stone)] flex items-center gap-1 mt-1"><Users className="h-3.5 w-3.5" /> {selected.signup_count} / {selected.max_volunteers} signed up · {selected.role}</div>
              {selected.description && <p className="mt-3 text-sm">{selected.description}</p>}
              {(selected.kennel_card?.dog_name || selected.kennel_card?.has_photo || selected.kennel_card?.dog_notes) && (
                <div className="mt-4 p-3 rounded-2xl bg-[color:var(--yellow)]/25 border border-[color:var(--yellow)]" data-testid="shift-signup-kennel-card">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--ink)] mb-2">Today's kennel card</div>
                  <div className="flex gap-3 items-start">
                    {selected.kennel_card?.has_photo && (
                      <img
                        data-testid="shift-signup-kennel-photo"
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/shifts/${selected.shift_id}/kennel-card-photo`}
                        alt={selected.kennel_card?.dog_name || "Dog"}
                        className="h-20 w-20 rounded-2xl object-cover shrink-0 border border-[color:var(--border)] bg-white"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {selected.kennel_card?.dog_name && (
                        <div className="font-display text-lg leading-tight" data-testid="shift-signup-kennel-name">{selected.kennel_card.dog_name}</div>
                      )}
                      {selected.kennel_card?.dog_notes && (
                        <p className="text-xs text-[color:var(--ink)] mt-1 whitespace-pre-wrap" data-testid="shift-signup-kennel-notes">{selected.kennel_card.dog_notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {selected?.is_signed_up && (
            <div className="flex flex-col gap-3 pt-1" data-testid="signup-dialog-calendar-buttons">
              <a
                data-testid="signup-dialog-google"
                href={googleAddUrl(selected)}
                target="_blank" rel="noreferrer"
                onClick={() => { toast.success("Opening Google Calendar…"); setTimeout(() => setSelected(null), 300); }}
                className="w-full btn-pill btn-primary text-sm inline-flex items-center justify-center gap-2 py-3"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
                Add to Google Calendar
              </a>
              <a
                data-testid="signup-dialog-apple"
                href={appleAddUrl(selected)}
                onClick={() => { toast.success("Opening Calendar app…"); setTimeout(() => setSelected(null), 300); }}
                className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                Add to Apple Calendar (iPhone / Mac)
              </a>
              <a
                data-testid="signup-dialog-outlook"
                href={outlookAddUrl(selected)}
                target="_blank" rel="noreferrer"
                onClick={() => { toast.success("Opening Outlook…"); setTimeout(() => setSelected(null), 300); }}
                className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.5 6.5v11c0 .83-.67 1.5-1.5 1.5H10v-2h9v-9L13 4l-3 2V4l3-2 8.5 4.5zM8 8h1v8H8V8zm-3 2h1v4H5v-4zm5-2h5v8h-5V8z"/></svg>
                Add to Outlook Calendar
              </a>
              <div className="text-xs text-[color:var(--stone)] text-center pt-1">
                Your calendar will open with this shift ready to save — one tap and you&rsquo;re done.
              </div>
            </div>
          )}

          <DialogFooter className="!flex-col-reverse sm:!flex-row sm:justify-between gap-2">
            <button data-testid="shift-signup-cancel-btn" onClick={()=>setSelected(null)} className="btn-pill btn-ghost-ink text-sm">Close</button>
            {selected?.is_signed_up ? (
              <button data-testid="shift-cancel-signup-btn" disabled={busy} onClick={()=>cancelSignup(selected)} className="btn-pill text-sm bg-[color:var(--red)]/15 text-[color:var(--red)] hover:bg-[color:var(--red)]/25">{busy ? "Cancelling…" : "Cancel my signup"}</button>
            ) : (
              <button data-testid="shift-signup-confirm-btn" disabled={busy} onClick={()=>claim(selected)} className="btn-pill btn-primary text-sm">{busy ? "Signing up…" : "Confirm sign-up"}</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Quick-add dialog removed — merged into shift-signup-dialog above */}

      <Dialog open={calendarOpen} onOpenChange={(v) => { setCalendarOpen(v); if (!v) setCalendarStep("choose"); }}>
        <DialogContent data-testid="calendar-picker-dialog" className="rounded-2xl max-w-md bg-white">
          {calendarStep === "choose" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add my shifts to my calendar</DialogTitle>
                <DialogDescription className="text-[color:var(--ink)] text-base pt-2">
                  <span className="inline-block bg-[color:var(--yellow)]/60 text-[color:var(--ink)] font-semibold rounded-full px-3 py-1 text-xs mb-2">One-time setup</span>
                  <br />
                  Which calendar app do you use? After this, <span className="font-semibold">every new shift you sign up for shows up automatically</span> — you never need to do this again.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 pt-2">
                <button
                  data-testid="calendar-picker-google"
                  onClick={() => setCalendarStep("google")}
                  className="w-full btn-pill btn-primary text-sm inline-flex items-center justify-center gap-2 py-3"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
                  Google Calendar
                </button>
                <button
                  data-testid="calendar-picker-apple"
                  onClick={() => setCalendarStep("apple")}
                  className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                  Apple Calendar (iPhone / Mac)
                </button>
                <button
                  data-testid="calendar-picker-outlook"
                  onClick={() => setCalendarStep("outlook")}
                  className="w-full btn-pill btn-ghost-ink text-sm inline-flex items-center justify-center gap-2 py-3"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.5 6.5v11c0 .83-.67 1.5-1.5 1.5H10v-2h9v-9L13 4l-3 2V4l3-2 8.5 4.5zM8 8h1v8H8V8zm-3 2h1v4H5v-4zm5-2h5v8h-5V8z"/></svg>
                  Outlook Calendar
                </button>
              </div>
              <DialogFooter>
                <button data-testid="calendar-picker-close" onClick={()=>setCalendarOpen(false)} className="btn-pill btn-ghost-ink text-sm">Not now</button>
              </DialogFooter>
            </>
          )}

          {calendarStep !== "choose" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {calendarStep === "google" && "Almost there — Google Calendar"}
                  {calendarStep === "apple" && "Almost there — Apple Calendar"}
                  {calendarStep === "outlook" && "Almost there — Outlook Calendar"}
                </DialogTitle>
                <DialogDescription className="text-[color:var(--ink)] text-base pt-2">
                  When you tap the button below, here&rsquo;s exactly what will happen:
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl bg-[color:var(--cream-2)] p-5 text-sm space-y-3" data-testid="calendar-picker-preview">
                {calendarStep === "google" && (
                  <>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>A new tab opens on google.com.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>Google asks <span className="font-semibold">&ldquo;Do you want to add this calendar?&rdquo;</span> — tap <span className="font-semibold">Add</span>.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Done. Your shifts show up in Google Calendar within a few minutes.</span></div>
                  </>
                )}
                {calendarStep === "apple" && (
                  <>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>The Calendar app opens on your iPhone or Mac.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>It shows a box that says <span className="font-semibold">&ldquo;Subscribe to Calendar&rdquo;</span> with a long web address — <span className="font-semibold">just tap Subscribe.</span> You don&rsquo;t need to change anything.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Tap <span className="font-semibold">Add</span> or <span className="font-semibold">Done</span> on the next screen. That&rsquo;s it — your shifts are in your calendar.</span></div>
                    <div className="mt-3 p-3 rounded-xl bg-[color:var(--yellow)]/40 text-xs">
                      <span className="font-semibold">Already subscribed?</span> If your calendar says <em>&ldquo;this subscription already exists&rdquo;</em>, you&rsquo;re all set — new shifts arrive automatically within an hour. No need to re-add.
                    </div>
                  </>
                )}
                {calendarStep === "outlook" && (
                  <>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">1.</span><span>A new tab opens on outlook.com.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">2.</span><span>Outlook may ask you to sign in, then shows an <span className="font-semibold">Import</span> button — tap it.</span></div>
                    <div className="flex gap-3"><span className="font-display text-xl leading-none text-[color:var(--red)]">3.</span><span>Done. Your shifts show up in Outlook within a few minutes.</span></div>
                  </>
                )}
              </div>
              <DialogFooter className="!flex-col-reverse sm:!flex-row sm:justify-end gap-2">
                <button data-testid="calendar-picker-back" onClick={()=>setCalendarStep("choose")} className="btn-pill btn-ghost-ink text-sm">Back</button>
                <button data-testid="calendar-picker-proceed" onClick={proceedToCalendar} className="btn-pill btn-primary text-sm">
                  Got it, open my calendar
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
